// src/components/InfoModal.tsx
import React from 'react';
import { Question } from '../types';
import './InfoModal.css';

interface InfoModalProps {
  question: Question | null;
  isOpen: boolean;
  onClose: () => void;
}

export const InfoModal: React.FC<InfoModalProps> = ({ question, isOpen, onClose }) => {
  if (!isOpen || !question || !question.tooltipContent) {
    return null;
  }

  const content = question.tooltipContent;
  const orgSize = localStorage.getItem('organizationSize') || 'small';

  return (
    <div className={`info-modal ${isOpen ? 'open' : 'closed'}`}>
      <div className="info-modal-backdrop" onClick={onClose} />
      <div className="info-modal-content">
        <div className="info-modal-header">
          <h2>Question Details</h2>
          <button className="info-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="info-modal-scroll">
          <section className="info-section">
            <h3>Question</h3>
            <p>{question.question}</p>
          </section>

          <section className="info-section">
            <h3>What This Means</h3>
            <p>{content.plainEnglish}</p>
          </section>

          {content.hipaaReference && (
            <section className="info-section">
              <h3>HIPAA Requirement</h3>
              <code className="regulation-ref">{content.hipaaReference}</code>
            </section>
          )}

          {content.realWorldExample && (
            <section className="info-section">
              <h3>Example</h3>
              <p>{content.realWorldExample}</p>
            </section>
          )}

          {content.implementationSteps && (
            <section className="info-section">
              <h3>How to Implement</h3>
              <ol>
                {content.implementationSteps.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </section>
          )}

          {content.commonMistakes && (
            <section className="info-section">
              <h3>Avoid These Mistakes</h3>
              <ul>
                {content.commonMistakes.map((mistake, idx) => (
                  <li key={idx}><span className="warning">⚠️</span> {mistake}</li>
                ))}
              </ul>
            </section>
          )}

          {content.estimatedCost && content.timeToComplete && (
            <section className="info-section info-grid">
              <div>
                <h4>Cost</h4>
                <p className="bold">
                  {orgSize === 'small' ? content.estimatedCost.small :
                   orgSize === 'medium' ? content.estimatedCost.medium :
                   content.estimatedCost.large}
                </p>
              </div>
              <div>
                <h4>Timeline</h4>
                <p className="bold">
                  {orgSize === 'small' ? content.timeToComplete.small :
                   orgSize === 'medium' ? content.timeToComplete.medium :
                   content.timeToComplete.large}
                </p>
              </div>
            </section>
          )}

          {content.resourceLinks && (
            <section className="info-section">
              <h3>Resources</h3>
              <div className="resources-mobile">
                {content.resourceLinks.map((link, idx) => (
                  <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="resource-link-mobile">
                    <span>{link.title}</span>
                    <span className="arrow">→</span>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
